import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { customerRequestOptions } from "../../services/customerService";
import * as XLSX from "xlsx";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FiAlertCircle,
  FiCalendar,
  FiClock,
  FiDownload,
  FiFileText,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import "./VoucherUsedHistory.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_POS_API_BASE_URL ||
  "https://doticom.vn:3000"
).replace(/\/+$/, "");

const getVietnamDateParts = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .formatToParts(date)
    .reduce((result, part) => {
      result[part.type] = part.value;
      return result;
    }, {});

const getDefaultHistoryRange = (date = new Date()) => {
  const parts = getVietnamDateParts(date);
  return {
    NgayBatDau: `${parts.year}-${parts.month}-01`,
    NgayKetThuc: `${parts.year}-${parts.month}-${parts.day}`,
  };
};

const getApiMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

const formatUsageDateTime = (value, part) => {
  if (!value) return "--";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  if (part === "date-key") {
    const dateParts = getVietnamDateParts(date);
    return `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
  }

  return new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    ...(part === "time"
      ? { hour: "2-digit", minute: "2-digit", second: "2-digit" }
      : { day: "2-digit", month: "2-digit", year: "numeric" }),
  }).format(date);
};

const normalizeHistory = (rows) =>
  (Array.isArray(rows) ? rows : [])
    .map((row, index) => ({
      ...row,
      _rowKey: `${String(row?.SDT || "unknown").trim()}-${
        row?.NgaySuDung || "unknown"
      }-${index}`,
      SDT: String(row?.SDT || "").trim(),
    }))
    .sort(
      (first, second) =>
        new Date(second.NgaySuDung).getTime() - new Date(first.NgaySuDung).getTime(),
    );

const validateHistoryRange = ({ NgayBatDau, NgayKetThuc }) => {
  if (!NgayBatDau || !NgayKetThuc) {
    return "Vui lòng chọn đầy đủ ngày bắt đầu và ngày kết thúc.";
  }
  if (NgayBatDau > NgayKetThuc) {
    return "Ngày bắt đầu không được lớn hơn ngày kết thúc.";
  }
  return "";
};

const VoucherUsedHistory = () => {
  const [filters, setFilters] = useState(getDefaultHistoryRange);
  const [appliedRange, setAppliedRange] = useState(getDefaultHistoryRange);
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const initialRangeRef = useRef(filters);
  const requestIdRef = useRef(0);

  const fetchHistory = useCallback(async (payload) => {
    const validationError = validateHistoryRange(payload);
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/voucher-used-history`,
        payload,
        customerRequestOptions(),
      );
      if (requestId !== requestIdRef.current) return;

      if (response.data?.success === false) {
        throw new Error(response.data.message || "API trả về kết quả không thành công.");
      }

      setHistory(normalizeHistory(response.data?.data));
      setAppliedRange(payload);
      setHasSearched(true);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setHistory([]);
      setHasSearched(true);
      setErrorMessage(
        getApiMessage(error, "Không thể tải lịch sử sử dụng voucher. Vui lòng thử lại."),
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchHistory(initialRangeRef.current);
  }, [fetchHistory]);

  const phoneUsageCounts = useMemo(() => {
    const counts = new Map();
    history.forEach((item) => {
      const phone = item.SDT || "--";
      counts.set(phone, (counts.get(phone) || 0) + 1);
    });
    return counts;
  }, [history]);

  const filteredHistory = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return history;

    return history.filter((item) => {
      const date = formatUsageDateTime(item.NgaySuDung, "date");
      const time = formatUsageDateTime(item.NgaySuDung, "time");
      return `${item.SDT} ${date} ${time}`.toLowerCase().includes(normalizedSearch);
    });
  }, [history, searchTerm]);

  const summary = useMemo(() => ({
    totalUsage: history.length,
    totalCustomers: new Set(
      history.map((item) => item.SDT).filter(Boolean),
    ).size,
    totalUsageDays: new Set(
      history
        .map((item) => formatUsageDateTime(item.NgaySuDung, "date-key"))
        .filter((dateKey) => dateKey !== "--"),
    ).size,
  }), [history]);

  const visibleCustomerCount = useMemo(
    () => new Set(filteredHistory.map((item) => item.SDT).filter(Boolean)).size,
    [filteredHistory],
  );

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
    setErrorMessage("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    fetchHistory(filters);
  };

  const handleExportExcel = () => {
    if (filteredHistory.length === 0) return;

    const detailRows = filteredHistory.map((item, index) => ({
      STT: index + 1,
      "Số điện thoại": item.SDT || "--",
      "Ngày sử dụng": formatUsageDateTime(item.NgaySuDung, "date"),
      "Giờ sử dụng": formatUsageDateTime(item.NgaySuDung, "time"),
      "Tổng lượt của SĐT": phoneUsageCounts.get(item.SDT || "--") || 0,
    }));
    const summaryRows = [
      { "Chỉ tiêu": "Từ ngày", "Giá trị": appliedRange.NgayBatDau },
      { "Chỉ tiêu": "Đến ngày", "Giá trị": appliedRange.NgayKetThuc },
      { "Chỉ tiêu": "Tổng lượt sử dụng", "Giá trị": summary.totalUsage },
      { "Chỉ tiêu": "Tổng khách hàng", "Giá trị": summary.totalCustomers },
      { "Chỉ tiêu": "Số ngày có sử dụng", "Giá trị": summary.totalUsageDays },
      { "Chỉ tiêu": "Số dòng đang xuất", "Giá trị": filteredHistory.length },
    ];

    const workbook = XLSX.utils.book_new();
    const detailWorksheet = XLSX.utils.json_to_sheet(detailRows);
    const summaryWorksheet = XLSX.utils.json_to_sheet(summaryRows);
    detailWorksheet["!cols"] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 16 },
      { wch: 14 },
      { wch: 20 },
    ];
    summaryWorksheet["!cols"] = [{ wch: 24 }, { wch: 18 }];

    XLSX.utils.book_append_sheet(workbook, detailWorksheet, "Lịch sử sử dụng");
    XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Tổng hợp");
    XLSX.writeFile(
      workbook,
      `lich-su-voucher_${appliedRange.NgayBatDau}_den_${appliedRange.NgayKetThuc}.xlsx`,
    );
  };

  return (
    <div className="voucher-history-page">
      <main className="container voucher-history-container py-4 py-lg-5">
        <header className="voucher-history-header">
          <div>
            <p className="voucher-history-eyebrow mb-2">CRM · VOUCHER</p>
            <h1 className="voucher-history-title">Lịch sử sử dụng voucher</h1>
            <p className="voucher-history-subtitle mb-0">
              Tra cứu, tổng hợp và xuất dữ liệu voucher đã sử dụng.
            </p>
          </div>
          <a href="/voucher-management" className="btn voucher-history-back-link">
            <FiFileText aria-hidden="true" /> Quản lý voucher
          </a>
        </header>

        <section className="voucher-history-filter-card" aria-label="Bộ lọc lịch sử voucher">
          <form onSubmit={handleSubmit}>
            <div className="voucher-history-filter-grid">
              <div>
                <label htmlFor="voucher-history-start" className="form-label">
                  Từ ngày
                </label>
                <div className="voucher-history-date-wrap">
                  <FiCalendar aria-hidden="true" />
                  <input
                    id="voucher-history-start"
                    name="NgayBatDau"
                    type="date"
                    className="form-control"
                    value={filters.NgayBatDau}
                    onChange={handleFilterChange}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="voucher-history-end" className="form-label">
                  Đến ngày
                </label>
                <div className="voucher-history-date-wrap">
                  <FiCalendar aria-hidden="true" />
                  <input
                    id="voucher-history-end"
                    name="NgayKetThuc"
                    type="date"
                    className="form-control"
                    value={filters.NgayKetThuc}
                    onChange={handleFilterChange}
                    disabled={isLoading}
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn voucher-history-search-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                    Đang tải
                  </>
                ) : (
                  <>
                    <FiSearch aria-hidden="true" />
                    Tra cứu
                  </>
                )}
              </button>
            </div>
          </form>

          {errorMessage && (
            <div className="alert alert-danger voucher-history-error mb-0" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}
        </section>

        <section className="voucher-history-summary" aria-label="Tổng hợp lịch sử voucher">
          <article className="voucher-history-summary-card is-usage">
            <span className="voucher-history-summary-icon"><FiFileText /></span>
            <div>
              <span>Tổng lượt sử dụng</span>
              <strong>{summary.totalUsage}</strong>
            </div>
          </article>
          <article className="voucher-history-summary-card is-customer">
            <span className="voucher-history-summary-icon"><FiUsers /></span>
            <div>
              <span>Tổng khách hàng</span>
              <strong>{summary.totalCustomers}</strong>
            </div>
          </article>
          <article className="voucher-history-summary-card is-day">
            <span className="voucher-history-summary-icon"><FiCalendar /></span>
            <div>
              <span>Số ngày có sử dụng</span>
              <strong>{summary.totalUsageDays}</strong>
            </div>
          </article>
        </section>

        <section className="voucher-history-table-card">
          <div className="voucher-history-table-heading">
            <div>
              <h2>Bảng lịch sử</h2>
              <p className="mb-0">
                {appliedRange.NgayBatDau} đến {appliedRange.NgayKetThuc}
              </p>
            </div>
            <button
              type="button"
              className="btn voucher-history-export-button"
              onClick={handleExportExcel}
              disabled={isLoading || filteredHistory.length === 0}
            >
              <FiDownload aria-hidden="true" />
              Xuất Excel
            </button>
          </div>

          <div className="voucher-history-toolbar">
            <div className="voucher-history-search-wrap">
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                className="form-control"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Tìm số điện thoại, ngày hoặc giờ..."
                aria-label="Tìm trong lịch sử voucher"
              />
            </div>
            <span className="voucher-history-visible-count">
              {filteredHistory.length} lượt · {visibleCustomerCount} khách hàng
            </span>
          </div>

          {isLoading && !hasSearched ? (
            <div className="voucher-history-state" role="status">
              <div className="spinner-border" aria-hidden="true" />
              <span>Đang tải lịch sử sử dụng voucher...</span>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="voucher-history-state is-empty">
              <FiFileText aria-hidden="true" />
              <strong>Chưa có dữ liệu phù hợp</strong>
              <span>Hãy thay đổi khoảng ngày hoặc nội dung tìm kiếm.</span>
            </div>
          ) : (
            <div className="table-responsive voucher-history-table-wrap">
              <table className="table voucher-history-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Số điện thoại</th>
                    <th>Ngày sử dụng</th>
                    <th>Giờ sử dụng</th>
                    <th className="text-end">Tổng lượt / SĐT</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((item, index) => (
                    <tr key={item._rowKey}>
                      <td className="text-secondary">{index + 1}</td>
                      <td><span className="voucher-history-phone">{item.SDT || "--"}</span></td>
                      <td>
                        <span className="voucher-history-date">
                          <FiCalendar aria-hidden="true" />
                          {formatUsageDateTime(item.NgaySuDung, "date")}
                        </span>
                      </td>
                      <td>
                        <span className="voucher-history-time">
                          <FiClock aria-hidden="true" />
                          {formatUsageDateTime(item.NgaySuDung, "time")}
                        </span>
                      </td>
                      <td className="text-end fw-bold">
                        {phoneUsageCounts.get(item.SDT || "--") || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan="4">Tổng đang hiển thị</th>
                    <th className="text-end">{filteredHistory.length} lượt</th>
                  </tr>
                  <tr>
                    <th colSpan="4">Tổng khách hàng đang hiển thị</th>
                    <th className="text-end">{visibleCustomerCount} khách hàng</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export {
  formatUsageDateTime,
  getDefaultHistoryRange,
  normalizeHistory,
  validateHistoryRange,
};
export default VoucherUsedHistory;
